import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import { Warning } from "@mui/icons-material";
import { ExportRequest } from "../../api/import_export";

interface ExportConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  exportRequest: ExportRequest;
  totalPhrases?: number;
  brandNamesMap?: Record<string, string>;
}

const ExportConfirmationDialog: React.FC<ExportConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  exportRequest,
  totalPhrases,
  brandNamesMap,
}) => {
  const hasFilters =
    exportRequest.query ||
    exportRequest.brand_ids?.length ||
    exportRequest.status_filter ||
    exportRequest.original_language_filter ||
    exportRequest.used_phrases_only ||
    exportRequest.suitability_filter;

  const getBrandNames = () => {
    if (!exportRequest.brand_ids?.length || !brandNamesMap) return null;
    return exportRequest.brand_ids
      .map((id) => brandNamesMap[id] || id)
      .join(", ");
  };

  const formatSuitability = (filter: string) => {
    const map: Record<string, string> = {
      title: "Title",
      description: "Description",
      bullet_points: "Bullet Points",
    };
    return map[filter] || filter;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Confirm Export</DialogTitle>
      <DialogContent>
        {/* Warning for exporting all */}
        {!hasFilters && (
          <Alert severity="warning" icon={<Warning />} sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="medium">
              No filters applied
            </Typography>
            <Typography variant="body2">
              You are about to export the entire phrasebook. This may take a
              while for large datasets.
            </Typography>
          </Alert>
        )}

        {/* Phrase count */}
        {totalPhrases !== undefined && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              bgcolor: totalPhrases === 0 ? "error.50" : "primary.50",
              borderRadius: 1,
              border: "1px solid",
              borderColor: totalPhrases === 0 ? "error.200" : "primary.200",
            }}
          >
            <Typography
              variant="h6"
              color={totalPhrases === 0 ? "error.main" : "primary.main"}
              fontWeight="bold"
            >
              {totalPhrases.toLocaleString()} phrases
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {totalPhrases === 0
                ? "No phrases match the current filters"
                : "will be exported"}
            </Typography>
          </Box>
        )}

        {/* Filter details */}
        {hasFilters && (
          <Box>
            <Typography variant="subtitle2" gutterBottom color="text.secondary">
              Export Filters
            </Typography>
            <Table size="small">
              <TableBody>
                {exportRequest.query && (
                  <TableRow>
                    <TableCell
                      component="th"
                      sx={{ fontWeight: 500, width: 140, border: "none", py: 0.5 }}
                    >
                      Search
                    </TableCell>
                    <TableCell sx={{ border: "none", py: 0.5 }}>
                      "{exportRequest.query}"
                    </TableCell>
                  </TableRow>
                )}
                {exportRequest.brand_ids?.length && (
                  <TableRow>
                    <TableCell
                      component="th"
                      sx={{ fontWeight: 500, width: 140, border: "none", py: 0.5 }}
                    >
                      Brands
                    </TableCell>
                    <TableCell sx={{ border: "none", py: 0.5 }}>
                      {getBrandNames()}
                    </TableCell>
                  </TableRow>
                )}
                {exportRequest.status_filter && (
                  <TableRow>
                    <TableCell
                      component="th"
                      sx={{ fontWeight: 500, width: 140, border: "none", py: 0.5 }}
                    >
                      Status
                    </TableCell>
                    <TableCell sx={{ border: "none", py: 0.5 }}>
                      {exportRequest.status_filter}
                    </TableCell>
                  </TableRow>
                )}
                {exportRequest.original_language_filter && (
                  <TableRow>
                    <TableCell
                      component="th"
                      sx={{ fontWeight: 500, width: 140, border: "none", py: 0.5 }}
                    >
                      Language
                    </TableCell>
                    <TableCell sx={{ border: "none", py: 0.5 }}>
                      {exportRequest.original_language_filter.toUpperCase()}
                    </TableCell>
                  </TableRow>
                )}
                {exportRequest.used_phrases_only && (
                  <TableRow>
                    <TableCell
                      component="th"
                      sx={{ fontWeight: 500, width: 140, border: "none", py: 0.5 }}
                    >
                      Usage
                    </TableCell>
                    <TableCell sx={{ border: "none", py: 0.5 }}>
                      Used phrases only
                    </TableCell>
                  </TableRow>
                )}
                {exportRequest.suitability_filter && (
                  <TableRow>
                    <TableCell
                      component="th"
                      sx={{ fontWeight: 500, width: 140, border: "none", py: 0.5 }}
                    >
                      Suitable for
                    </TableCell>
                    <TableCell sx={{ border: "none", py: 0.5 }}>
                      {formatSuitability(exportRequest.suitability_filter)}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={totalPhrases === 0}
        >
          Start Export
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportConfirmationDialog;
